import React, { useState, useEffect } from 'react';
import { 
  Star, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Trash2, 
  Eye, 
  Filter,
  Search,
  RefreshCw,
  MessageSquare
} from 'lucide-react';

const ReviewsManagement = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    rating: '',
    search: ''
  });
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [selectedReview, setSelectedReview] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchReviews();
    fetchStats();
  }, [page, filters]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        ...(filters.status && { status: filters.status }),
        ...(filters.rating && { rating: filters.rating.toString() })
      });

      const response = await fetch(`/backend/api/reviews.php?path=all&${params}`);
      const data = await response.json();
      
      if (data.success) {
        setReviews(data.reviews || []);
        setPagination(data.pagination || {});
      }
    } catch (error) {
      console.error('Greška pri učitavanju recenzija:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch('/backend/api/reviews.php?path=stats');
      const data = await response.json();
      
      if (data.success) {
        setStats(data.stats || {});
      }
    } catch (error) {
      console.error('Greška pri učitavanju statistika:', error);
    }
  };

  const updateReviewStatus = async (reviewId, newStatus) => {
    try {
      const response = await fetch('/backend/api/reviews.php?path=status', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: reviewId, status: newStatus })
      });

      const data = await response.json();
      
      if (data.success) {
        fetchReviews();
        fetchStats();
        if (selectedReview?.id === reviewId) {
          setSelectedReview(prev => ({ ...prev, status: newStatus }));
        }
      } else {
        alert('Greška pri ažuriranju statusa: ' + (data.error || 'Nepoznata greška'));
      }
    } catch (error) {
      console.error('Greška:', error);
      alert('Greška pri ažuriranju statusa');
    }
  };

  const deleteReview = async (reviewId) => {
    if (!confirm('Da li ste sigurni da želite da obrišete ovu recenziju?')) {
      return;
    }

    try {
      const response = await fetch(`/backend/api/reviews.php?path=review&id=${reviewId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      
      if (data.success) {
        fetchReviews();
        fetchStats();
        setShowModal(false);
      } else {
        alert('Greška pri brisanju: ' + (data.error || 'Nepoznata greška'));
      }
    } catch (error) {
      console.error('Greška:', error);
      alert('Greška pri brisanju recenzije');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock, text: 'Na čekanju' },
      approved: { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle, text: 'Odobreno' },
      rejected: { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle, text: 'Odbijeno' }
    };
    
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating 
          ? 'text-yellow-400 fill-yellow-400' 
          : 'text-gray-300'
        }`}
      />
    ));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('sr-RS', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredReviews = reviews.filter(review => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        review.name.toLowerCase().includes(searchLower) ||
        review.email.toLowerCase().includes(searchLower) ||
        review.review_text.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-background border border-border rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-muted-foreground">Ukupno</p>
              <p className="text-2xl font-bold text-foreground">{stats.total || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-background border border-border rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-4 h-4 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-muted-foreground">Na čekanju</p>
              <p className="text-2xl font-bold text-foreground">{stats.pending || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-background border border-border rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-muted-foreground">Odobreno</p>
              <p className="text-2xl font-bold text-foreground">{stats.approved || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-background border border-border rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <Star className="w-4 h-4 text-purple-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-muted-foreground">Prosek</p>
              <p className="text-2xl font-bold text-foreground">
                {stats.average_rating ? parseFloat(stats.average_rating).toFixed(1) : '0.0'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-background border border-border rounded-lg p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Pretraga..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="px-3 py-2 border border-border rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="px-3 py-2 border border-border rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">Svi statusi</option>
            <option value="pending">Na čekanju</option>
            <option value="approved">Odobreno</option>
            <option value="rejected">Odbijeno</option>
          </select>
          
          <select
            value={filters.rating}
            onChange={(e) => setFilters(prev => ({ ...prev, rating: e.target.value }))}
            className="px-3 py-2 border border-border rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">Sve ocene</option>
            <option value="5">5 zvezda</option>
            <option value="4">4 zvezde</option>
            <option value="3">3 zvezde</option>
            <option value="2">2 zvezde</option>
            <option value="1">1 zvezda</option>
          </select>
          
          <button
            onClick={() => {
              setFilters({ status: '', rating: '', search: '' });
              setPage(1);
            }}
            className="flex items-center gap-2 px-3 py-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Resetuj
          </button>
        </div>
      </div>

      {/* Reviews Table */}
      <div className="bg-background border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Učitavanje recenzija...</p>
          </div>
        ) : filteredReviews.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Nema recenzija</h3>
            <p className="text-muted-foreground">Nema recenzija koje odgovaraju vašim filterima.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Korisnik
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Ocena
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Datum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Akcije
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredReviews.map((review) => (
                  <tr key={review.id} className="hover:bg-muted/50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-foreground">{review.name}</div>
                        <div className="text-sm text-muted-foreground">{review.email}</div>
                        <div className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          "{review.review_text.substring(0, 100)}..."
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        {renderStars(review.rating)}
                        <span className="ml-1 text-sm text-muted-foreground">({review.rating})</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(review.status)}
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {formatDate(review.created_at)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedReview(review);
                            setShowModal(true);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Pogledaj detalje"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        
                        {review.status === 'pending' && (
                          <>
                            <button
                              onClick={() => updateReviewStatus(review.id, 'approved')}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Odobri"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => updateReviewStatus(review.id, 'rejected')}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Odbij"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        
                        <button
                          onClick={() => deleteReview(review.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Obriši"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-border">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Prikazano {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} od {pagination.total} recenzija
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(prev => Math.max(1, prev - 1))}
                  disabled={pagination.page <= 1}
                  className="px-3 py-1 border border-border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted transition-colors"
                >
                  Prethodna
                </button>
                <span className="px-3 py-1 text-sm text-muted-foreground">
                  {pagination.page} / {pagination.pages}
                </span>
                <button
                  onClick={() => setPage(prev => Math.min(pagination.pages, prev + 1))}
                  disabled={pagination.page >= pagination.pages}
                  className="px-3 py-1 border border-border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted transition-colors"
                >
                  Sledeća
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Review Detail Modal */}
      {showModal && selectedReview && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-foreground">Detalji recenzije</h3>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">Ime</label>
                  <p className="text-foreground">{selectedReview.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">Email</label>
                  <p className="text-foreground">{selectedReview.email}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">Ocena</label>
                  <div className="flex items-center gap-1">
                    {renderStars(selectedReview.rating)}
                    <span className="ml-1 text-foreground">({selectedReview.rating}/5)</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">Status</label>
                  {getStatusBadge(selectedReview.status)}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Recenzija</label>
                <p className="text-foreground bg-muted p-4 rounded-lg">
                  "{selectedReview.review_text}"
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">Kreiran</label>
                  <p className="text-foreground">{formatDate(selectedReview.created_at)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">Ažuriran</label>
                  <p className="text-foreground">{formatDate(selectedReview.updated_at)}</p>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t border-border">
                {selectedReview.status === 'pending' && (
                  <>
                    <button
                      onClick={() => updateReviewStatus(selectedReview.id, 'approved')}
                      className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Odobri
                    </button>
                    <button
                      onClick={() => updateReviewStatus(selectedReview.id, 'rejected')}
                      className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                    >
                      <XCircle className="w-4 h-4" />
                      Odbij
                    </button>
                  </>
                )}
                <button
                  onClick={() => deleteReview(selectedReview.id)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Obriši
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewsManagement;